import { CalendarIcon, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';
import { DateRangePanel } from './date-range-panel';
import { type DateRangePreset, type DateRangeValue, dateRangeLabel } from './model';

/**
 * Compact date-range control: one button showing the current selection that
 * opens a popover with a preset sidebar plus a two-month range calendar
 * (the pattern used in the CRM app). It's a thin skin over the shared
 * `DateRangeValue` model + presets, so callers keep the same value shape
 * (`preset` + custom `from`/`to`, incl. `all_time`).
 *
 * Use this where a toolbar needs a small footprint; use `DateRangeFilter`
 * (inline preset dropdown + From/To fields) where a form has room to spread.
 */
export interface DateRangePopoverProps {
  value: DateRangeValue;
  /** Presets to offer in the sidebar (order preserved). */
  presets: readonly DateRangePreset[];
  onChange: (next: DateRangeValue) => void;
  id?: string;
  className?: string;
}

export function DateRangePopover({
  value,
  presets,
  onChange,
  id,
  className,
}: DateRangePopoverProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          size="sm"
          // See the matching comment in `date-picker.tsx`: `PopoverTrigger
          // asChild` clobbers this Button's `data-slot="button"`, so the
          // shared pill-radius rule never matches — set it directly.
          className={cn('h-8 justify-start gap-2 rounded-full font-normal', className)}
        >
          <CalendarIcon className="size-4 text-muted-foreground" />
          <span className="max-w-[18rem] truncate">{dateRangeLabel(value, true)}</span>
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent data-picker="date" className="w-auto p-0" align="start">
        <DateRangePanel
          value={value}
          presets={presets}
          onChange={onChange}
          onPresetPicked={(_, shouldClose) => {
            if (shouldClose) setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
